import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, CalendarDays, Rows3, ListOrdered, Archive } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { tourApi } from "@/lib/tourEntities";
import { customerApi } from "@/lib/entities";
import { contractApi } from "@/lib/phase3Entities";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { DemoChip, StatusPill } from "@/components/Chips";
import { RoleGate } from "@/components/RoleGate";
import { Pager } from "@/pages/customers/CustomersList";
import { TourFormDialog } from "@/pages/tours/TourFormDialog";
import { MobileListCard } from "@/components/MobileListCard";
import { ActionMenu, ArchiveConfirmModal, useArchive } from "@/components/ActionMenu";
import { TourTimeline } from "@/pages/tours/TourTimeline";

const READ = ["super_admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance", "driver"];
// Wave item 2 (ops-ux-documents-2026-09-23) — the Tours workspace now
// defaults to the Timeline view. User's explicit choice is remembered
// for the current browser session ONLY via sessionStorage under
// `aa_tours_view_pref` (a plain view identifier string — NEVER auth
// tokens, cookies, or PII). Closing the tab clears the preference.
const VIEW_PREF_KEY = "aa_tours_view_pref";
const ALLOWED_VIEWS = ["timeline", "list"];
const DEFAULT_VIEW = "timeline";

// Correction Batch STEP 3 — canonical sort options (mirrors the
// server's `_TOUR_SORTS` in backend/api/tours.py). Preserved in the URL
// as `?sort=<key>` so browser Back / share-link restore the ordering.
const SORT_OPTIONS = [
  { key: "soonest_first",  label: "Soonest First" },
  { key: "furthest_first", label: "Furthest First" },
  { key: "name_asc",       label: "Tour Name A–Z" },
  { key: "updated_desc",   label: "Recently Updated" },
];
const DEFAULT_SORT = "soonest_first";
const ALLOWED_SORTS = SORT_OPTIONS.map((s) => s.key);

// Archived tab identifier — reflected in URL as `?tab=archived`. Reuses
// the existing `tours.view` permission; no new permission introduced.
const ALLOWED_TABS = ["active", "archived"];
const DEFAULT_TAB = "active";

const _readViewPref = () => {
  try {
    const v = window.sessionStorage.getItem(VIEW_PREF_KEY);
    return ALLOWED_VIEWS.includes(v) ? v : null;
  } catch {
    return null;
  }
};

const _writeViewPref = (v) => {
  try {
    if (ALLOWED_VIEWS.includes(v)) window.sessionStorage.setItem(VIEW_PREF_KEY, v);
  } catch {
    /* ignore quota / disabled storage */
  }
};
// Wave 4B-5 P1: write gate is now capability-based via can("tours.create").
// Backend authoritative (perm("tours.create") on POST /tours). Retained
// READ role list because backend read paths still key by role.

export default function ToursList() {
  const { user, can } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [page, setPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  // View selection precedence: explicit ?view=... in the URL (deep
  // link) → sessionStorage remembered choice → DEFAULT_VIEW.
  const _initView = () => {
    const urlView = searchParams.get("view");
    if (ALLOWED_VIEWS.includes(urlView)) return urlView;
    return _readViewPref() || DEFAULT_VIEW;
  };
  const [view, setView] = useState(_initView);
  // Correction Batch STEP 3 — sort + tab (active/archived).
  const _initSort = () => {
    const s = searchParams.get("sort");
    return ALLOWED_SORTS.includes(s) ? s : DEFAULT_SORT;
  };
  const _initTab = () => {
    const t = searchParams.get("tab");
    return ALLOWED_TABS.includes(t) ? t : DEFAULT_TAB;
  };
  const [sort, setSort] = useState(_initSort);
  const [tab, setTab] = useState(_initTab);
  const canWrite = can("tours.create");

  // Persist explicit user choices to sessionStorage AND reflect the
  // current view in the URL (?view=…) so Back navigation from a tour
  // detail returns to the same visual context — including the current
  // filter (q, status) and view mode. React Router keeps the browser
  // history in sync with `setSearchParams`; combined with the initial
  // hydration precedence above we get full state survival across Back.
  const _setView = (v) => {
    if (!ALLOWED_VIEWS.includes(v)) return;
    setView(v);
    _writeViewPref(v);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("view", v);
    setSearchParams(nextParams, { replace: true });
  };

  // Phase C Wave 2A — honour ?status=... deep links from dashboard widgets
  // so navigating from e.g. "1st Holds" tile pre-filters the list.
  useEffect(() => {
    const s = searchParams.get("status") || "";
    setStatus(s);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const load = async () => {
    try {
      // Correction Batch STEP 3 — Active tab shows upcoming tours sorted
      // per the current selection; Archived tab narrows to
      // `is_archived=true`. Server owns the predicate + sort so
      // pagination is correct.
      const params = {
        q: q || undefined,
        status: status || undefined,
        page,
        page_size: 25,
        sort,
      };
      if (tab === "archived") {
        params.archived = true;
      } else {
        params.upcoming = true;
      }
      const d = await tourApi.list(params);
      setRows(d.results || []);
      setTotal(d.total || 0);
    } catch {
      toast.error("Failed to load tours");
    }
  };

  const archive = useArchive({
    entityLabel: "Tour",
    apiArchive: tourApi.archive,
    description:
      "It will be removed from active tour lists. Assignments, holds, quotes, contracts, pickup sheets, and audit history are preserved.",
    checkBlockers: async (row) => {
      try {
        const cs = await contractApi.list(row.id);
        return (cs || [])
          .filter((c) => c.status === "executed")
          .map((c) => `contract ${c.contract_number || c.id.slice(0, 8)} (executed)`);
      } catch {
        return [];
      }
    },
    onSuccess: async (row) => {
      toast.success(`Archived ${row.display_name}`);
      await load();
    },
  });
  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [q, status, page, sort, tab]);
  useEffect(() => {
    customerApi.list({ page_size: 200 }).then((d) => setCustomers(d.results)).catch(() => {});
  }, []);

  // Wave item 2 — reflect the active filters into the URL so the
  // browser Back button restores the same state (q + status + view +
  // page). Uses replace to avoid piling history entries per keystroke.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (q) next.set("q", q); else next.delete("q");
    if (status) next.set("status", status); else next.delete("status");
    next.set("view", view);
    if (sort !== DEFAULT_SORT) next.set("sort", sort); else next.delete("sort");
    if (tab !== DEFAULT_TAB) next.set("tab", tab); else next.delete("tab");
    if (page > 1) next.set("page", String(page)); else next.delete("page");
    setSearchParams(next, { replace: true });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [q, status, view, sort, tab, page]);

  // Hydrate q from URL once on mount so the URL is the source of
  // truth for Back navigation. Status hydration already happens in
  // the deep-link effect above.
  useEffect(() => {
    const urlQ = searchParams.get("q") || "";
    if (urlQ && !q) setQ(urlQ);
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    if (urlPage > 1 && page === 1) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RoleGate permission="tours.view">
      <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Tours</SectionHeader>
        <PageHeader
          testid="page-tours"
          title="Tours"
          subtitle={`${total.toLocaleString()} total`}
          right={
            <div className="flex flex-wrap items-center gap-2" data-testid="tours-header-right">
              {/* RC9 · Sole Tours view-control group. The RC8-added
                  compound switcher was DELETED because it duplicated
                  the individual Timeline, List, Schedule, and New
                  Tour controls below. State-preservation semantics
                  (sessionStorage `aa_tours_view_pref` + URL
                  query-string carry across Tours ↔ Schedule) moved
                  INTO this existing right-side code path. */}
              {/* View toggle — Timeline is default. sessionStorage
                  remembers the user's choice for this browser session
                  and the URL also carries ?view=… so Back preserves
                  the visual context. */}
              <div
                className="inline-flex items-center rounded-sm border border-coach-silver overflow-hidden"
                role="tablist"
                aria-label="Tours view mode"
                data-testid="tours-view-mode"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === "timeline"}
                  data-testid="tours-view-mode-timeline"
                  onClick={() => _setView("timeline")}
                  className={
                    "inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium " +
                    (view === "timeline"
                      ? "bg-coach-blue text-white"
                      : "bg-white text-carbon hover:bg-road-white")
                  }
                >
                  <Rows3 size={14} /> Timeline
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === "list"}
                  data-testid="tours-view-mode-list"
                  onClick={() => _setView("list")}
                  className={
                    "inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium border-l border-coach-silver " +
                    (view === "list"
                      ? "bg-coach-blue text-white"
                      : "bg-white text-carbon hover:bg-road-white")
                  }
                >
                  <ListOrdered size={14} /> List
                </button>
              </div>
              <Link
                to={`/schedule${loc.search || ""}`}
                data-testid="tours-view-schedule"
                onClick={() => {
                  // Persist current view choice so returning to Tours
                  // via Timeline or List restores the right pane.
                  try { window.sessionStorage.setItem(VIEW_PREF_KEY, view); } catch { /* non-fatal */ }
                }}
                aria-current={undefined}
                className="inline-flex items-center gap-1.5 border border-coach-silver hover:border-coach-blue text-carbon text-[12.5px] font-medium px-3 py-1.5 rounded-sm bg-white"
              >
                <CalendarDays size={14} /> Schedule
              </Link>
              {canWrite && (
                <button
                  data-testid="new-tour-btn"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm"
                >
                  <Plus size={14} /> New Tour
                </button>
              )}
            </div>
          }
        />

        <div className="bg-white border border-coach-silver rounded-md">
          {/* STEP 3 — Active vs Archived tab. Reuses `tours.view` perm. */}
          <div
            className="flex items-center gap-1 px-4 pt-3"
            role="tablist"
            aria-label="Tours active/archived tab"
            data-testid="tours-tab-group"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === "active"}
              data-testid="tours-tab-active"
              onClick={() => { setTab("active"); setPage(1); }}
              className={
                "inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium rounded-t-sm border border-b-0 " +
                (tab === "active"
                  ? "bg-coach-blue text-white border-coach-blue"
                  : "bg-white text-carbon border-coach-silver hover:bg-road-white")
              }
            >
              Active Tours
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "archived"}
              data-testid="tours-tab-archived"
              onClick={() => { setTab("archived"); setPage(1); }}
              className={
                "inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium rounded-t-sm border border-b-0 " +
                (tab === "archived"
                  ? "bg-coach-blue text-white border-coach-blue"
                  : "bg-white text-carbon border-coach-silver hover:bg-road-white")
              }
            >
              <Archive size={13} /> Archived Tours
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-t border-coach-silver">
            <input
              data-testid="tours-search"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder={tab === "archived" ? "Search archived by tour #, name, artist…" : "Search by tour #, name, artist…"}
              className="flex-1 max-w-md border border-coach-silver rounded-sm px-3 py-1.5 text-[13px]"
            />
            <select
              data-testid="tours-filter-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border border-coach-silver rounded-sm px-2 py-1.5 text-[12.5px] bg-white"
            >
              <option value="">All statuses</option>
              {[
                "inquiry", "availability_review", "soft_hold", "first_hold", "second_hold",
                "contract_pending", "booked", "dispatch_ready", "dispatched",
                "active_tour", "completed", "billing", "closed", "cancelled",
              ].map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              ))}
            </select>
            <label className="text-[10.5px] uppercase tracking-widest text-steel ml-auto">Sort</label>
            <select
              data-testid="tours-sort"
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
              className="border border-coach-silver rounded-sm px-2 py-1.5 text-[12.5px] bg-white"
            >
              {SORT_OPTIONS.map((s) => (
                <option key={s.key} value={s.key} data-testid={`tours-sort-option-${s.key}`}>{s.label}</option>
              ))}
            </select>
          </div>
          {view === "timeline" && (
            <TourTimeline
              rows={rows}
              canWrite={canWrite}
              onEdit={(r) => nav(`/tours/${r.id}?edit=1`, { state: { from: loc.pathname + loc.search } })}
              onArchive={(r) => archive.request(r)}
              backState={{ from: loc.pathname + loc.search }}
            />
          )}
          <table className={(view === "list" ? "hidden md:table" : "hidden") + " w-full text-[13px]"} data-testid="tours-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-4 py-2 w-28">Tour #</th>
                <th className="text-left px-4 py-2">Display Name</th>
                <th className="text-left px-4 py-2">Artist</th>
                <th className="text-left px-4 py-2 w-56">Dates</th>
                <th className="text-left px-4 py-2 w-36">Status</th>
                <th className="text-right px-4 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rows && rows.map((r) => (
                <tr key={r.id} data-testid={`tour-row-${r.tour_number}`} className="border-t border-coach-silver/60 hover:bg-road-white">
                  <td className="px-4 py-2 font-mono text-[12px] text-steel">{r.tour_number}</td>
                  <td className="px-4 py-2">
                    <Link to={`/tours/${r.id}`} className="text-coach-blue hover:underline">{r.display_name}</Link>{" "}
                    {r.is_demo && <DemoChip />}
                  </td>
                  <td className="px-4 py-2 text-steel">{r.artist_display || "—"}</td>
                  <td className="px-4 py-2 text-steel">{r.start_date} → {r.end_date}</td>
                  <td className="px-4 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-2 py-2 text-right">
                    <ActionMenu
                      testid={`tour-actions-${r.tour_number}`}
                      onView={() => nav(`/tours/${r.id}`)}
                      onEdit={canWrite ? () => nav(`/tours/${r.id}?edit=1`) : undefined}
                      onArchive={canWrite && !r.is_archived ? () => archive.request(r) : undefined}
                    />
                  </td>
                </tr>
              ))}
              {rows === null && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-steel text-[13px]">Loading tours…</td></tr>
              )}
              {rows && rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-steel">No tours found.</td></tr>
              )}
            </tbody>
          </table>

          {/* Mobile cards — shown for the List view only. Timeline
              renders its own responsive cards which already work on
              small viewports. */}
          <div className={(view === "list" ? "md:hidden " : "hidden ") + "space-y-3 p-3"} data-testid="tours-mobile-list">
            {rows === null && (
              <div className="text-center text-steel text-[13px] py-8">Loading tours…</div>
            )}
            {rows && rows.length === 0 && (
              <div className="text-center text-steel text-[13px] py-8">No tours found.</div>
            )}
            {(rows || []).map((r) => (
              <MobileListCard
                key={r.id}
                to={`/tours/${r.id}`}
                testid={`tour-card-${r.tour_number}`}
                monoTag={r.tour_number}
                primary={r.display_name}
                secondary={
                  <>
                    {r.artist_display && <>{r.artist_display} · </>}
                    {r.start_date} → {r.end_date}
                  </>
                }
                status={r.status}
                isDemo={r.is_demo}
                right={
                  <ActionMenu
                    testid={`tour-actions-mobile-${r.tour_number}`}
                    onView={() => nav(`/tours/${r.id}`)}
                    onEdit={canWrite ? () => nav(`/tours/${r.id}?edit=1`) : undefined}
                    onArchive={canWrite && !r.is_archived ? () => archive.request(r) : undefined}
                  />
                }
              />
            ))}
          </div>

          <Pager page={page} setPage={setPage} total={total} pageSize={25} />
        </div>

        {open && (
          <TourFormDialog
            open={open}
            customers={customers}
            onClose={() => setOpen(false)}
            onSaved={() => { setOpen(false); load(); }}
          />
        )}
        <ArchiveConfirmModal {...archive.modalProps} />
      </PageShell>
    </RoleGate>
  );
}
